import {
  UPLOAD_COLUMNS,
  type UploadLayoutDefinition,
  type UploadSourceTransform,
} from '@erp/rpc';

import {
  AS400_UPLOAD_FIELDS,
  type As400UploadField,
} from './as400UploadFields';

type Mapping = UploadLayoutDefinition['columns'][number]['mapping'];

const TEXT: UploadSourceTransform = { type: 'text' };

function uploadColumn(index: number) {
  const column = UPLOAD_COLUMNS[index];
  if (!column) throw new Error(`No upload column ${index}`);
  return column;
}

const COST_ADD_FIELD_COLUMN = uploadColumn(
  AS400_UPLOAD_FIELDS.indexOf('COST ADD FIELD'),
);

/** The new margin: the prior one plus Feedback's Margin Increase, in points. */
export const FEEDBACK_NEW_MARGIN =
  '[FINAL PRIOR MARGINS] + DEFAULT([Feedback: Margin Increase], 0) / 100';

/**
 * The columns of a layout that applies a supplier's Feedback back to its
 * results (Stelpro, Hubbell): the workbook already carries the 82 upload
 * columns in order, so each passes through by position except:
 *
 * - SELL NET: `sellNet`, rounded to 2 decimals.
 * - COST ADD FIELD: the new cost adder in points, unrounded (the pricing
 *   team's own formula is ×100 with no ROUND); blank when there is none.
 * - COST ADD FLAG: the workbook's own value, cleared when COST ADD FIELD is
 *   blank.
 */
export function feedbackLayoutColumns({
  headers,
  sellNet,
  newCostAdder,
}: {
  /** The workbook's header for each of the 82 upload columns, in order. */
  headers: readonly string[];
  /** The SELL NET formula. */
  sellNet: string;
  /** The new cost adder as a fraction, e.g. 0.03 for 3 points. */
  newCostAdder: string;
}): UploadLayoutDefinition['columns'] {
  if (headers.length !== AS400_UPLOAD_FIELDS.length) {
    throw new Error(`Expected ${AS400_UPLOAD_FIELDS.length} headers`);
  }
  const calculated: Partial<Record<As400UploadField, Mapping>> = {
    'SELL NET': {
      kind: 'formula',
      expression: sellNet,
      decimals: 2,
      blankWhenZero: false,
    },
    'COST ADD FIELD': {
      kind: 'formula',
      expression: `(${newCostAdder}) * 100`,
      decimals: null,
      blankWhenZero: true,
    },
  };
  return AS400_UPLOAD_FIELDS.map((field, index) => {
    const header = headers[index] ?? field;
    return {
      column: uploadColumn(index),
      mapping:
        field === 'COST ADD FLAG'
          ? {
              kind: 'source',
              header,
              transform: TEXT,
              onlyWhenFilled: COST_ADD_FIELD_COLUMN,
            }
          : (calculated[field] ?? { kind: 'source', header, transform: TEXT }),
    };
  });
}
